/**
 * Reusable in-line entity-name links.
 *
 * Renders an underlined link to the entity's detail page when:
 *   • the entity id is present, AND
 *   • the current user holds the corresponding view permission.
 * Otherwise falls back to plain text so the UI never shows a dead link.
 *
 * Style: dotted underline on hover, subtle accent color, no icon by default
 * (to avoid noise in dense tables). Pass `withIcon` to opt in.
 */
import { Link } from 'react-router-dom';
import { User, Briefcase, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const _baseClass =
  'inline text-foreground hover:text-primary hover:underline decoration-dotted underline-offset-2 transition-colors';

const _build = ({ to, name, fallback, requiredPerm, withIcon, Icon, testId }) => {
  // We can't call hooks inside helpers — wrappers below call useAuth().
  return { to, name, fallback, requiredPerm, withIcon, Icon, testId };
};

const _Link = ({ to, name, fallback, requiredPerm, withIcon, Icon, testId }) => {
  const { hasPermission } = useAuth();
  const text = name || fallback || '—';
  if (!to || !hasPermission(requiredPerm)) {
    return <span className="text-muted-foreground" data-testid={testId ? `${testId}-text` : undefined}>{text}</span>;
  }
  return (
    <Link
      to={to}
      className={_baseClass}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      {withIcon && Icon && <Icon className="inline h-3 w-3 mr-1 -mt-0.5 opacity-60" />}
      {text}
    </Link>
  );
};

export const EmployeeLink = ({ id, name, fallback = 'Unknown', withIcon, testId }) =>
  _Link(_build({
    to: id ? `/employees/${id}` : null,
    name, fallback, requiredPerm: 'employees.view',
    withIcon, Icon: User, testId: testId || (id ? `employee-link-${id}` : 'employee-link'),
  }));

export const ProjectLink = ({ id, name, fallback = 'Unknown project', withIcon, testId }) =>
  _Link(_build({
    to: id ? `/projects/${id}` : null,
    name, fallback, requiredPerm: 'projects.view',
    withIcon, Icon: Briefcase, testId: testId || (id ? `project-link-${id}` : 'project-link'),
  }));

export const ClientLink = ({ id, name, fallback = 'Unknown client', withIcon, testId }) =>
  _Link(_build({
    to: id ? `/clients/${id}` : null,
    name, fallback, requiredPerm: 'clients.view',
    withIcon, Icon: Building2, testId: testId || (id ? `client-link-${id}` : 'client-link'),
  }));
